"use client";

import { Control, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { format, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import GenericLoader from "@/components/GenericLoader";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as UICalendar } from "@/components/ui/calendar";

type LessonOption = {
  id: string;
  workspace_id: string;
  topic: string;
  start_time?: string | null;
  start_time_formatted?: string | null;
  subject?: { id: string; name: string; icon?: string } | null;
};

interface LessonStepProps {
  control: Control<any>;
}

export function LessonStep({ control }: LessonStepProps) {
  const { t, i18n } = useTranslation(["finances", "common", "workspaces"]);
  const selectedType = useWatch({ control, name: "type" });
  const formContext = useFormContext();

  const [lessons, setLessons] = useState<LessonOption[]>([]);
  const [isLoadingLessons, setIsLoadingLessons] = useState(true);
  const [lessonsIdQuery, setLessonsIdQuery] = useState("");
  const [lessonsTopicQuery, setLessonsTopicQuery] = useState("");
  const [lessonsSubjectQuery, setLessonsSubjectQuery] = useState("");
  const [lessonsWorkspaceQuery, setLessonsWorkspaceQuery] = useState("");
  const [lessonsDateQuery, setLessonsDateQuery] = useState<Date | undefined>(undefined);

  const fetchLessons = useDebouncedCallback(
    async (
      topicQuery: string,
      idQuery: string,
      subjectQuery: string,
      workspaceQuery: string,
      dateQuery?: Date,
    ) => {
      if (selectedType !== "lesson") return;
      setIsLoadingLessons(true);
      try {
        const params: Record<string, string> = {};

        if (idQuery.trim()) params.id = idQuery.trim();
        if (topicQuery.trim().length >= 3) params.topic = topicQuery.trim();
        if (subjectQuery.trim().length >= 3) params.subject = subjectQuery.trim();
        if (workspaceQuery.trim().length >= 3) params.workspace = workspaceQuery.trim();
        if (dateQuery) params.date = format(dateQuery, "yyyy-MM-dd");
        const { data } = await axiosApiInstance.get(API_ENDPOINTS.TUTOR.FINANCES.PAYMENTS_LESSONS, {
          params,
        });

        setLessons(data.data || []);
      } catch (e) {
        setLessons([]);
      } finally {
        setIsLoadingLessons(false);
      }
    },
    500,
  );

  useEffect(() => {
    if (selectedType !== "lesson") return;
    fetchLessons(
      lessonsTopicQuery,
      lessonsIdQuery,
      lessonsSubjectQuery,
      lessonsWorkspaceQuery,
      lessonsDateQuery,
    );
  }, [
    selectedType,
    lessonsTopicQuery,
    lessonsIdQuery,
    lessonsSubjectQuery,
    lessonsWorkspaceQuery,
    lessonsDateQuery,
    fetchLessons,
  ]);

  return (
    <FormField
      control={control}
      name="lesson_id"
      render={({ field }) => (
        <FormItem className="mb-16 flex h-full flex-col px-6 py-6 max-sm:px-4 max-sm:py-4">
          {/* Header */}
          <div className="mb-6 text-center">
            <h1 className="text-accent-foreground mb-3 flex items-center justify-center gap-3 text-2xl font-bold max-sm:text-xl">
              <Icon
                className="text-muted-foreground"
                height="1.75rem"
                icon="tabler:book"
                width="1.75rem"
              />
              {t("finances:createPayment.steps.selectLesson")}
            </h1>
            <p className="text-muted-foreground mx-auto max-w-md text-base max-sm:text-sm">
              {t("finances:createPayment.steps.lessonDesc")}
            </p>
          </div>

          {/* Collapsible filters */}
          <div className="mb-6">
            <div className="mx-auto max-w-4xl space-y-3">
              {/* First row: Date, Subject, ID */}
              <div className="flex gap-3">
                {/* Date */}
                <div className="bg-muted/20 flex-1 rounded-xl p-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        className="flex h-auto w-full items-center justify-between bg-transparent p-0 hover:bg-transparent"
                        variant="ghost"
                      >
                        <span className="inline-flex items-center gap-2 text-sm font-medium">
                          <Icon height="1rem" icon="tabler:calendar" width="1rem" />
                          {t("finances:createPayment.filters.date", "Date")}
                        </span>
                        <Icon height="1rem" icon="tabler:chevron-down" width="1rem" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="bg-foreground z-[2000] w-fit p-2">
                      <UICalendar
                        initialFocus
                        className={"w-full"}
                        locale={getDateFnsLocale(i18n.language)}
                        mode="single"
                        selected={lessonsDateQuery as any}
                        weekStartsOn={1}
                        onSelect={(d: any) => setLessonsDateQuery(d ?? undefined)}
                      />
                      <div className="mt-2 flex items-center justify-between">
                        <Button
                          className="h-8 w-full"
                          variant="ghost"
                          onClick={() => setLessonsDateQuery(undefined)}
                        >
                          {t("common:clear", "Clear")}
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Subject */}
                <div className="bg-muted/20 flex-1 rounded-xl p-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        className="flex h-auto w-full items-center justify-between bg-transparent p-0 hover:bg-transparent"
                        variant="ghost"
                      >
                        <span className="inline-flex items-center gap-2 text-sm font-medium">
                          <Icon height="1rem" icon="tabler:category" width="1rem" />
                          {t("finances:createPayment.filters.subject", "Subject")}
                        </span>
                        <Icon height="1rem" icon="tabler:chevron-down" width="1rem" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="bg-foreground z-[2000] w-[var(--radix-popover-trigger-width)] max-w-sm p-2"
                    >
                      <div className="space-y-3">
                        <div className="relative">
                          <Icon
                            className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
                            height="1rem"
                            icon="tabler:search"
                            width="1rem"
                          />
                          <Input
                            className="bg-muted/30 focus:bg-muted/20 h-10 rounded-lg pr-3 pl-9 text-sm"
                            placeholder={t(
                              "finances:createPayment.filters.subjectPlaceholder",
                              "Search by subject...",
                            )}
                            value={lessonsSubjectQuery}
                            onChange={(e) => setLessonsSubjectQuery(e.target.value)}
                          />
                        </div>
                        {lessonsSubjectQuery && lessonsSubjectQuery.trim().length < 3 && (
                          <p className="text-muted-foreground text-xs">
                            {t(
                              "finances:createPayment.filters.minChars",
                              "Type at least 3 characters",
                            )}
                          </p>
                        )}
                        <div className="flex items-center justify-between">
                          <Button
                            className="h-8 w-full"
                            variant="ghost"
                            onClick={() => setLessonsSubjectQuery("")}
                          >
                            {t("common:clear", "Clear")}
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* ID */}
                <div className="bg-muted/20 flex-1 rounded-xl p-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        className="flex h-auto w-full items-center justify-between bg-transparent p-0 hover:bg-transparent"
                        variant="ghost"
                      >
                        <span className="inline-flex items-center gap-2 text-sm font-medium">
                          <Icon height="1rem" icon="tabler:hash" width="1rem" />
                          {t("finances:createPayment.filters.lessonId", "Lesson ID")}
                        </span>
                        <Icon height="1rem" icon="tabler:chevron-down" width="1rem" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="bg-foreground z-[2000] w-[var(--radix-popover-trigger-width)] max-w-sm p-2"
                    >
                      <div className="space-y-3">
                        <div className="relative">
                          <Icon
                            className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
                            height="1rem"
                            icon="tabler:search"
                            width="1rem"
                          />
                          <Input
                            className="bg-muted/30 focus:bg-muted/20 h-10 rounded-lg pr-3 pl-9 text-sm"
                            placeholder={t(
                              "finances:createPayment.lessonIdSearchPlaceholder",
                              "Search by lesson ID...",
                            )}
                            value={lessonsIdQuery}
                            onChange={(e) => setLessonsIdQuery(e.target.value)}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Button
                            className="h-8 w-full"
                            variant="ghost"
                            onClick={() => setLessonsIdQuery("")}
                          >
                            {t("common:clear", "Clear")}
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Second row: Topic and Workspace */}
              <div className="flex gap-3">
                {/* Topic */}
                <div className="bg-muted/20 flex-1 rounded-xl p-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        className="flex h-auto w-full items-center justify-between bg-transparent p-0 hover:bg-transparent"
                        variant="ghost"
                      >
                        <span className="inline-flex items-center gap-2 text-sm font-medium">
                          <Icon height="1rem" icon="tabler:book" width="1rem" />
                          {t("finances:createPayment.filters.topic", "Topic")}
                        </span>
                        <Icon height="1rem" icon="tabler:chevron-down" width="1rem" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="bg-foreground z-[2000] w-[var(--radix-popover-trigger-width)] max-w-sm p-2"
                    >
                      <div className="space-y-3">
                        <div className="relative">
                          <Icon
                            className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
                            height="1rem"
                            icon="tabler:search"
                            width="1rem"
                          />
                          <Input
                            className="bg-muted/30 focus:bg-muted/20 h-10 rounded-lg pr-3 pl-9 text-sm"
                            placeholder={t(
                              "finances:createPayment.lessonTopicPlaceholder",
                              "Search by lesson topic...",
                            )}
                            value={lessonsTopicQuery}
                            onChange={(e) => setLessonsTopicQuery(e.target.value)}
                          />
                        </div>
                        {lessonsTopicQuery && lessonsTopicQuery.trim().length < 3 && (
                          <p className="text-muted-foreground text-xs">
                            {t(
                              "finances:createPayment.filters.minChars",
                              "Type at least 3 characters",
                            )}
                          </p>
                        )}
                        <div className="flex items-center justify-between">
                          <Button
                            className="h-8 w-full"
                            variant="ghost"
                            onClick={() => setLessonsTopicQuery("")}
                          >
                            {t("common:clear", "Clear")}
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Workspace */}
                <div className="bg-muted/20 flex-1 rounded-xl p-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        className="flex h-auto w-full items-center justify-between bg-transparent p-0 hover:bg-transparent"
                        variant="ghost"
                      >
                        <span className="inline-flex items-center gap-2 text-sm font-medium">
                          <Icon height="1rem" icon="tabler:building" width="1rem" />
                          {t("finances:createPayment.filters.workspace", "Workspace")}
                        </span>
                        <Icon height="1rem" icon="tabler:chevron-down" width="1rem" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="bg-foreground z-[2000] w-[var(--radix-popover-trigger-width)] max-w-sm p-2"
                    >
                      <div className="space-y-3">
                        <div className="relative">
                          <Icon
                            className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
                            height="1rem"
                            icon="tabler:search"
                            width="1rem"
                          />
                          <Input
                            className="bg-muted/30 focus:bg-muted/20 h-10 rounded-lg pr-3 pl-9 text-sm"
                            placeholder={t(
                              "finances:createPayment.filters.workspacePlaceholder",
                              "Search by workspace...",
                            )}
                            value={lessonsWorkspaceQuery}
                            onChange={(e) => setLessonsWorkspaceQuery(e.target.value)}
                          />
                        </div>
                        {lessonsWorkspaceQuery && lessonsWorkspaceQuery.trim().length < 3 && (
                          <p className="text-muted-foreground text-xs">
                            {t(
                              "finances:createPayment.filters.minChars",
                              "Type at least 3 characters",
                            )}
                          </p>
                        )}
                        <div className="flex items-center justify-between">
                          <Button
                            className="h-8 w-full"
                            variant="ghost"
                            onClick={() => setLessonsWorkspaceQuery("")}
                          >
                            {t("common:clear", "Clear")}
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          </div>

          {/* Form Message - below filters */}
          <FormMessage className="mx-auto mb-4 max-w-lg" />

          {/* Lesson List */}
          <div className="flex-1">
            {isLoadingLessons ? (
              <div className={"relative min-h-[300px]"}>
                <GenericLoader className={"absolute rounded-xl"} />
              </div>
            ) : (
              <div className="bg-muted/20 h-full rounded-xl p-3">
                {lessons.length > 0 ? (
                  <ScrollArea className="h-full">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {lessons.map((lesson, index) => (
                        <motion.button
                          key={lesson.id}
                          animate={{ opacity: 1, y: 0 }}
                          className={`group relative overflow-hidden rounded-lg p-4 text-left ${
                            field.value === lesson.id
                              ? "bg-accent-foreground shadow-sm"
                              : "bg-muted/30 hover:bg-muted/50 hover:shadow-sm"
                          }`}
                          initial={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2, delay: Math.min(0.05 * index, 0.3) }}
                          type="button"
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => {
                            field.onChange(lesson.id);
                            try {
                              formContext.setValue(
                                "lesson_topic" as any,
                                (lesson.topic || "") as any,
                              );
                              formContext.setValue(
                                "lesson_start_time" as any,
                                (lesson.start_time || null) as any,
                              );
                              formContext.setValue(
                                "lesson_subject_name" as any,
                                (lesson.subject?.name || null) as any,
                              );
                            } catch (e) {}
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                                field.value === lesson.id ? "bg-muted" : "bg-muted/50"
                              }`}
                            >
                              <Icon
                                className={"text-muted-foreground"}
                                height="1.25rem"
                                icon={lesson.subject?.icon || "tabler:book"}
                                width="1.25rem"
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3
                                className={`truncate text-sm font-semibold ${
                                  field.value === lesson.id
                                    ? "text-foreground"
                                    : "text-accent-foreground"
                                }`}
                              >
                                {lesson.topic || t("workspaces:untitledLesson")}
                              </h3>
                              {lesson.subject && (
                                <p
                                  className={`mt-1 text-xs ${
                                    field.value === lesson.id
                                      ? "text-foreground"
                                      : "text-muted-foreground"
                                  }`}
                                >
                                  {lesson.subject.name}
                                </p>
                              )}
                              {lesson.start_time && (
                                <p
                                  className={`mt-1 text-xs ${
                                    field.value === lesson.id
                                      ? "text-primary"
                                      : "text-accent-foreground"
                                  }`}
                                >
                                  {format(parseISO(lesson.start_time), "PPP", {
                                    locale: getDateFnsLocale(i18n.language),
                                  })}
                                </p>
                              )}
                            </div>

                            {/* Selection indicator */}
                            {field.value === lesson.id && (
                              <motion.div
                                animate={{ scale: 1, opacity: 1 }}
                                className="absolute top-3 right-3"
                                initial={{ scale: 0, opacity: 0 }}
                              >
                                <div className="bg-muted/50 flex h-5 w-5 items-center justify-center rounded-full">
                                  <Icon
                                    className="text-foreground"
                                    height="0.75rem"
                                    icon="tabler:check"
                                    width="0.75rem"
                                  />
                                </div>
                              </motion.div>
                            )}
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-muted-foreground flex h-full flex-col items-center justify-center text-center">
                    <Icon
                      className="mx-auto mb-4 opacity-50"
                      height="2.5rem"
                      icon="tabler:book-off"
                      width="2.5rem"
                    />
                    <h3 className="text-accent-foreground mb-2 text-base font-semibold">
                      {t("finances:createPayment.noLessons")}
                    </h3>
                    <p className="text-muted-foreground max-w-xs text-sm">
                      {t("finances:createPayment.noLessonsDesc")}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </FormItem>
      )}
    />
  );
}
